<template>
  <div class="bo-wrapper" :class="computer ? 'bo-wrapper-pc' : ''">
    <header class="ant-layout-header">
      <div>BOBO公式</div>
      <div class="formula-label">公式三(散光人工晶体)</div>
    </header>
    <a-layout-content style="padding: 0 20px">
      <div class="toric-page">
        <div class="toric-heading">
          <div>
            <h2>散光人工晶体度数计算器</h2>
            <p>双角度矢量法 · 近轴光学转换 · Baylor 后表面散光预测 <em>注：对话框里面均需要填入对应数值</em></p>
          </div>
          <span>仅供临床参考</span>
        </div>

        <div class="toric-patient">
          <label>患者 <a-input v-model:value="patient.name" placeholder="姓名" /></label>
          <label>病历号 <a-input v-model:value="patient.id" placeholder="ID" /></label>
          <label>术眼
            <a-select v-model:value="toricEye"><a-select-option value="OD">右眼</a-select-option><a-select-option value="OS">左眼</a-select-option></a-select>
          </label>
          <label>IOL等效球镜 <a-input v-model:value="activeToric.sphere" class="important-input" suffix="D" /></label>
          <label>残留屈光度 <a-input :value="activeToric.se" readonly suffix="D" /></label>
        </div>

        <div class="toric-input-grid">
          <div class="toric-card">
            <div class="card-title aca-title">
              <div>角膜前表面 <span class="title-badge">ACA / K值</span></div>
              <a-checkbox v-model:checked="activeToric.totalCorneal">全角膜散光模式</a-checkbox>
            </div>
            <div class="toric-row">
              <label>K1</label>
              <div class="toric-number-field"><a-input-number v-model:value="activeToric.k1" :min="0" :max="100" :step="0.01" /><span>D</span></div>
              <label>轴位</label>
              <div class="toric-number-field"><a-input-number v-model:value="activeToric.k1Axis" :min="0" :max="180" :step="1" /><span>°</span></div>
            </div>
            <div class="toric-row">
              <label>K2</label>
              <div class="toric-number-field"><a-input-number v-model:value="activeToric.k2" :min="0" :max="100" :step="0.01" /><span>D</span></div>
              <label>轴位</label>
              <div class="toric-number-field"><a-input-number v-model:value="activeToric.k2Axis" :min="0" :max="180" :step="1" /><span>°</span></div>
            </div>
            <div class="toric-result-line"><strong>ACA</strong>{{ formatToric(toricResult.acaCyl, toricResult.acaAxis) }}</div>
          </div>

          <div class="toric-card pca-card">
            <div class="card-title">角膜后表面散光 <span class="title-badge">PCA / Baylor</span></div>
            <div class="toric-hint">WTR阈值 1.7D · ATR阈值 0.4D · 基于 ACA 自动计算</div>
            <div class="pca-box">
              <div>{{ baylorDescription }}</div>
              <strong>{{ pcaDescription }}</strong>
            </div>
          </div>

          <div class="toric-card combined-card">
            <div class="combined-section">
              <div class="card-title">手术诱导散光 <span class="title-badge">SIA</span></div>
              <div class="toric-row">
                <label>度数</label>
                <div class="toric-number-field"><a-input-number v-model:value="activeToric.siaCyl" :min="0" :step="0.01" /><span>D</span></div>
                <label>轴位</label>
                <div class="toric-number-field"><a-input-number v-model:value="activeToric.siaAxis" :min="0" :max="180" :step="1" /><span>°</span></div>
              </div>
            </div>
            <div class="combined-section optical-card">
              <div class="card-title">光学参数</div>
              <div class="optical-inputs">
                <label>K_avg <div class="toric-number-field"><a-input-number :value="Number(toricKAvg)" disabled /><span>D</span></div></label>
                <!-- <label>角膜-IOL距 <div class="toric-number-field"><a-input-number v-model:value="activeToric.iolDist" :step="0.1" /><span>mm</span></div></label> -->
              </div>
            </div>
          </div>
        </div>

        <div class="toric-analysis">
          <div class="analysis-title">选定 Toric IOL · 残留分析 <span>轴位自动对齐</span></div>
          <div class="iol-summary">
            <label>IOL度数 <a-input v-model:value="activeToric.iolCyl" class="important-input" suffix="D" /></label>
            <label>IOL平面所需 <a-input :value="formatToric(toricResult.iolNeededCyl, toricResult.iolNeededAxis)" disabled /></label>
            <div class="axis-thumb">
              <div class="axis-circle">
                <i :style="axisLineStyle"></i><b>T</b>
                <span class="axis-degree axis-degree-top">0°</span>
                <span class="axis-degree axis-degree-current">{{ formatAxis(toricResult.iolNeededAxis) }}</span>
                <span class="axis-degree axis-degree-left">90°</span>
                <span class="axis-degree axis-degree-right">90°</span>
                <span class="axis-degree axis-degree-bottom">180°</span>
              </div>
              <div>目标 <strong>{{ formatAxis(toricResult.targetAxis) }}</strong><br />植入 <strong>{{ formatAxis(toricResult.iolNeededAxis) }}</strong></div>
            </div>
          </div>
          <div class="residual-grid">
            <div><span>选定 IOL</span><strong>{{ selectedIolText }}</strong></div>
            <div><span>残留散光 (IOL平面)</span><strong class="danger">{{ formatResidualToric(toricResult.residualCyl, toricResult.residualAxis) }}</strong></div>
            <div><span>残留散光 (角膜平面)</span><strong class="danger">{{ formatResidualToric(toricResult.residualCorneaCyl, toricResult.residualCorneaAxis) }}</strong></div>
          </div>
          <div class="rotation-title">旋转影响评估 (IOL平面)</div>
          <a-table class="rotation-table" :columns="toricColumns" :data-source="rotationRows" :pagination="false" size="small" row-key="rotationDeg" />
        </div>

        <div v-if="toricError" class="toric-error">计算异常：{{ toricError }}</div>
        <div class="toric-actions">
          <a-button @click="handleResetData">重置(reset)</a-button>
        </div>
      </div>
    </a-layout-content>
  </div>
</template>

<script setup>
import { computed, h, nextTick, onActivated, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { Toast, isComputer } from "@/utils";

const route = useRoute();
const computer = isComputer();
const patient = reactive({ name: "", id: "" });
const toricEye = ref("OD");
const toricError = ref("");
const toricResult = reactive({});
const initialized = ref(false);

const createToricState = () => reactive({
  k1: "42.00", k1Axis: "", k2: "44.00", k2Axis: "",
  siaCyl: "0.40", siaAxis: "180", iolDist: "4.0", iolCyl: "3.00",
  sphere: "-1.25", se: "", totalCorneal: false,
});
const odToric = createToricState();
const osToric = createToricState();
const activeToric = computed(() => toricEye.value === "OD" ? odToric : osToric);
const toricKAvg = computed(() => {
  const value = (Number(activeToric.value.k1) + Number(activeToric.value.k2)) / 2;
  return Number.isFinite(value) ? value.toFixed(1) : "--";
});
const formatAxis = (axis) => Number.isFinite(Number(axis)) ? `${Number(axis).toFixed(0)}°` : "--";
const formatToric = (cyl, axis) => Number.isFinite(Number(cyl)) && Number.isFinite(Number(axis)) ? `${Number(cyl).toFixed(2)} D @ ${Number(axis).toFixed(1)}°` : "--";
const formatResidualToric = (cyl, axis) => Number.isFinite(Number(cyl)) && Number.isFinite(Number(axis)) ? `${Number(cyl).toFixed(2)} D @ ${Number(axis).toFixed(1)} deg` : "--";
const normalizeAxis = (axis) => ((axis % 180) + 180) % 180;
const vectorAdd = (c1, a1, c2, a2) => {
  const x = c1 * Math.cos(2 * a1 * Math.PI / 180) + c2 * Math.cos(2 * a2 * Math.PI / 180);
  const y = c1 * Math.sin(2 * a1 * Math.PI / 180) + c2 * Math.sin(2 * a2 * Math.PI / 180);
  const cyl = Math.sqrt(x * x + y * y);
  return { cyl, axis: cyl < 0.0001 ? 0 : normalizeAxis(Math.atan2(y, x) * 90 / Math.PI) };
};
const getAcaType = (axis) => {
  const value = normalizeAxis(Number(axis));
  if (Math.abs(value - 90) <= 30) return "WTR";
  if (Math.min(value, 180 - value) <= 30) return "ATR";
  return "Oblique";
};
const baylorDescription = computed(() => {
  if (!Number.isFinite(Number(toricResult.acaCyl))) return "--";
  if (activeToric.value.totalCorneal) return "全角膜散光模式：后表面散光设为0";
  const target = vectorAdd(Number(toricResult.acaCyl), Number(toricResult.acaAxis), Number(toricResult.pcaCyl), Number(toricResult.pcaAxis));
  return `Baylor目标矫正：${formatToric(target.cyl, target.axis)} (ACA ${getAcaType(toricResult.acaAxis)} ${Number(toricResult.acaCyl).toFixed(2)}D)`;
});
const pcaDescription = computed(() => {
  if (!Number.isFinite(Number(toricResult.pcaCyl))) return "--";
  if (activeToric.value.totalCorneal) return "PCA = 0.00 D";
  return Number(toricResult.pcaCyl) > 0.001 ? `等效PCA：${formatToric(toricResult.pcaCyl, toricResult.pcaAxis)}` : "无需植入Toric IOL";
});
const selectedIolText = computed(() => Number.isFinite(Number(toricResult.iolNeededAxis)) ? formatToric(activeToric.value.iolCyl, toricResult.iolNeededAxis) : "--");
const axisLineStyle = computed(() => ({ transform: `rotate(${-Number(toricResult.iolNeededAxis || 0)}deg)` }));
const rotationRows = computed(() => {
  if (!Number.isFinite(Number(toricResult.iolNeededCyl))) return [];
  if (Array.isArray(toricResult.rotationImpacts)) return toricResult.rotationImpacts;
  return [0, 5, 10, 15, 20, 30, 45].map((rotationDeg) => {
    const effectiveRate = Math.cos(2 * rotationDeg * Math.PI / 180);
    const effectiveCyl = Number(activeToric.value.iolCyl) * effectiveRate;
    const residual = vectorAdd(Number(toricResult.iolNeededCyl), Number(toricResult.iolNeededAxis), -effectiveCyl, Number(toricResult.iolNeededAxis) + rotationDeg);
    return { rotationDeg, effectiveRate, residualCyl: residual.cyl, residualAxis: residual.axis };
  });
});
const getRotationLevel = (rotationDeg) => {
  const degree = Math.abs(Number(rotationDeg));
  if (degree === 0) return { text: "理想", type: "ideal" };
  if (degree <= 10) return { text: "轻微", type: "slight" };
  if (degree <= 15) return { text: "注意", type: "warning" };
  return { text: "严重", type: "severe" };
};
const toricColumns = [
  { title: "旋转角度", dataIndex: "rotationDeg", align: "center", customRender: ({ text }) => { const level = getRotationLevel(text); return h("span", { class: "rotation-angle" }, [`${text}° `, h("em", { class: `rotation-level rotation-${level.type}` }, level.text)]); } },
  { title: "矫正率", dataIndex: "effectiveRate", align: "center", customRender: ({ text, record }) => { const level = getRotationLevel(record.rotationDeg); return h("strong", { class: `rotation-rate rotation-${level.type}` }, `${(Number(text) * 100).toFixed(1)}%`); } },
  { title: "残留散光", align: "center", customRender: ({ record }) => formatResidualToric(record.residualCyl, record.residualAxis) },
];

const handleToricCalculate = async () => {
  const source = activeToric.value;
  const fields = [
    ["k1", "K1 度数"], ["k1Axis", "K1 轴位"],
    ["k2", "K2 度数"], ["k2Axis", "K2 轴位"],
    ["siaCyl", "手术诱导散光度数"], ["siaAxis", "手术诱导散光轴位"],
    ["iolDist", "角膜-IOL距"], ["iolCyl", "IOL 度数"],
  ];
  for (const [key, label] of fields) {
    const value = source[key];
    if (value === undefined || value === null || String(value).trim() === "") {
      Toast("error", `请输入${label}`, 3);
      return;
    }
    if (!Number.isFinite(Number(value))) {
      Toast("error", `${label}格式不正确，请输入有效数字`, 3);
      return;
    }
  }
  toricError.value = "";
  try {
    const response = await fetch("https://visionhealthmed.com/api-eye/toric-iol/calculate", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ k1Flat: Number(source.k1), k1FlatAxis: Number(source.k1Axis), k2Steep: Number(source.k2), k2SteepAxis: Number(source.k2Axis), siaCyl: Number(source.siaCyl), siaAxis: Number(source.siaAxis), iolDist: Number(source.iolDist), iolCyl: Number(source.iolCyl), totalCorneal: source.totalCorneal }),
    });
    if (!response.ok) throw new Error(`网络请求失败，状态码：${response.status}`);
    Object.keys(toricResult).forEach(key => delete toricResult[key]);
    Object.assign(toricResult, await response.json());
  } catch (error) {
    toricError.value = error.message || "请求失败";
  }
};

const resetState = (state) => Object.assign(state, { k1: "42.00", k1Axis: "", k2: "44.00", k2Axis: "", siaCyl: "0.40", siaAxis: "180", iolDist: "4.0", iolCyl: "3.00", sphere: "-1.25", se: "", totalCorneal: false });
const handleResetData = () => {
  resetState(activeToric.value);
  Object.keys(toricResult).forEach(key => delete toricResult[key]);
};
const hasValue = (value) => value !== undefined && value !== null && value !== "";
const initialize = async () => {
  initialized.value = false;
  if (route.query.data) {
    try {
      const data = JSON.parse(String(route.query.data));
      patient.name = data.name || "";
      patient.id = data.id || "";
      toricEye.value = String(data.eye).toUpperCase() === "OS" ? "OS" : "OD";
      const target = activeToric.value;
      if (hasValue(data.k1)) target.k1 = data.k1;
      if (hasValue(data.k2)) target.k2 = data.k2;
      if (hasValue(data.iol)) target.sphere = data.iol;
      target.se = hasValue(data.se) ? data.se : "";
      const L = hasValue(data.L) ? Number(data.L) : NaN;
      const k1 = hasValue(data.k1) ? Number(data.k1) : NaN;
      const k2 = hasValue(data.k2) ? Number(data.k2) : NaN;
      const constantA = hasValue(data.constantA) ? Number(data.constantA) : NaN;
      if ([L, k1, k2, constantA].every(Number.isFinite)) {
        const H = -10.82097721 + 0.31230173 * L + 0.0771381 * (k1 + k2);
        target.iolDist = (H + 0.62467 * constantA - 72.083).toFixed(2);
      }
    } catch (error) {
      toricError.value = "传入数据格式不正确";
    }
  }
  await nextTick();
  initialized.value = true;
  handleToricCalculate();
};

onActivated(initialize);

watch(toricEye, () => {
  if (!initialized.value) return;
  Object.keys(toricResult).forEach(key => delete toricResult[key]);
  handleToricCalculate();
});
let toricTimer;
watch(activeToric, () => {
  if (!initialized.value) return;
  clearTimeout(toricTimer);
  toricTimer = setTimeout(handleToricCalculate, 300);
}, { deep: true });
</script>

<style lang="less" scoped>
.bo-wrapper {
  height: 100vh;
  overflow-y: scroll;
  padding-bottom: 50px;
}

.ant-layout-header {
  padding: 0 10px;
  border-bottom: 1px solid #eee;
  margin-bottom: 1px;
  display: flex;
  justify-content: space-between;
  background: linear-gradient(135deg, #35b7ad 10%, #abdcff);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

.formula-label {
  font-size: 13px;
}

.toric-page {
  max-width: 940px;
  margin: 20px auto;
  color: #333;
}

.toric-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 22px;
  color: #fff;
  border-radius: 10px 10px 0 0;
  background: linear-gradient(135deg, #35b7ad 10%, #abdcff);

  h2 { margin: 0; color: #fff; font-size: 20px; }
  p { margin: 4px 0 0; font-size: 12px; opacity: .9; }
  p em { margin-left: 8px; color: #fff1b8; font-style: normal; }
  > span { padding: 3px 12px; border-radius: 16px; background: rgba(255, 255, 255, .2); font-size: 11px; }
}

.toric-patient {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
  padding: 12px 16px;
  background: #f6fbfb;
  border: 1px solid #d9eeee;

  label { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; }
  .ant-input-affix-wrapper, .ant-input { width: 120px; }
  .ant-select { width: 90px; }
}

.toric-input-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 18px 0;
}

.toric-card, .toric-analysis {
  padding: 14px 16px;
  border: 1px solid #d9eeee;
  border-radius: 10px;
  background: #f8fcfc;
}

.card-title, .analysis-title, .rotation-title {
  margin-bottom: 12px;
  color: #333;
  font-size: 15px;
  font-weight: 700;
}

.title-badge,
.analysis-title span {
  margin-left: 8px;
  padding: 1px 10px;
  color: #5abebf;
  border: 1px solid #d9eeee;
  border-radius: 12px;
  background: #fff;
  font-size: 10px;
  font-weight: 400;
}

.aca-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  .ant-checkbox-wrapper {
    color: #666;
    font-size: 11px;
    font-weight: 400;
  }
}

.toric-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 9px;

  label {
    width: 38px;
    text-align: right;
    color: #666;
    font-size: 12px;
  }

}

.toric-number-field {
  position: relative;
  width: 105px;

  .ant-input-number {
    width: 100%;
    background: #fffef7;
  }

  > span {
    position: absolute;
    z-index: 2;
    top: 50%;
    right: 8px;
    color: #999;
    font-size: 11px;
    pointer-events: none;
    transform: translateY(-50%);
  }

  :deep(.ant-input-number-input) {
    padding-right: 28px;
  }
}

.pca-card {
  display: flex;
  flex-direction: column;
}

.pca-card .pca-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.combined-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.combined-section + .combined-section {
  padding-top: 12px;
  border-top: 1px solid #d9eeee;
}

.combined-section .card-title {
  margin-bottom: 9px;
}

.optical-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    color: #666;
    font-size: 12px;
  }
}

.toric-result-line, .pca-box {
  margin-top: 10px;
  padding: 7px 12px;
  text-align: center;
  border-left: 3px solid #5abebf;
  border-radius: 4px;
  background: #eaf8f7;
  font-size: 12px;

  strong {
    margin-right: 10px;
    color: #35a9a5;
  }
}

.toric-hint {
  color: #999;
  text-align: center;
  font-size: 11px;
}

.pca-box strong {
  display: block;
  margin: 3px 0 0;
  color: #d48806;
}

.important-input {
  border-color: #f5222d;
  color: #f5222d;
  font-weight: 700;
}

.toric-analysis {
  margin-bottom: 15px;
}

.iol-summary {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
}

.iol-summary > label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.iol-summary .ant-input-affix-wrapper,
.iol-summary .ant-input {
  width: 160px;
}

.axis-thumb {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #666;
  font-size: 11px;
}

.axis-thumb strong {
  color: #35a9a5;
}

.axis-circle {
  position: relative;
  width: 64px;
  height: 64px;
  border: 1px solid #cfe6e5;
  border-radius: 50%;
  background: #fff;
}

.axis-circle:before,
.axis-circle:after {
  content: "";
  position: absolute;
  background: #e4eeee;
}

.axis-circle:before {
  left: 31px;
  top: 5px;
  width: 1px;
  height: 52px;
}

.axis-circle:after {
  top: 31px;
  left: 5px;
  width: 52px;
  height: 1px;
}

.axis-circle i {
  position: absolute;
  z-index: 2;
  left: 31px;
  top: 31px;
  width: 26px;
  height: 2px;
  transform-origin: left center;
  background: #35a9a5;
}

.axis-circle b {
  position: absolute;
  z-index: 3;
  left: 27px;
  top: 22px;
  color: #35a9a5;
}

.axis-degree {
  position: absolute;
  z-index: 4;
  color: #8a9b9b;
  font-size: 8px;
  line-height: 1;
  white-space: nowrap;
}

.axis-degree-top { left: 50%; top: -11px; transform: translateX(-50%); }
.axis-degree-current { left: 50%; top: 4px; transform: translateX(-50%); color: #1677ff; font-weight: 700; }
.axis-degree-left { left: -18px; top: 50%; transform: translateY(-50%); }
.axis-degree-right { right: -20px; top: 50%; transform: translateY(-50%); }
.axis-degree-bottom { left: 50%; bottom: -11px; transform: translateX(-50%); }

.residual-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin: 16px 0;
}

.residual-grid > div {
  padding: 10px;
  text-align: center;
  border: 1px solid #d9eeee;
  border-radius: 6px;
  background: #fff;
}

.residual-grid span,
.residual-grid strong {
  display: block;
}

.residual-grid span {
  color: #777;
  font-size: 11px;
}

.residual-grid strong {
  margin-top: 5px;
  color: #35a9a5;
  font-size: 14px;
}

.residual-grid strong.danger {
  color: #f5222d;
}

.rotation-title {
  margin-top: 8px;
}

.rotation-table {
  border: 1px solid #d9eeee;
  border-radius: 6px;
  overflow: hidden;
}

:deep(.rotation-level) {
  margin-left: 4px;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
}

:deep(.rotation-rate) {
  font-weight: 700;
}

:deep(.rotation-ideal),
:deep(.rotation-slight) {
  color: #1677ff;
}

:deep(.rotation-level.rotation-ideal),
:deep(.rotation-level.rotation-slight) {
  background: #e6f4ff;
}

:deep(.rotation-warning) {
  color: #fa8c16;
}

:deep(.rotation-level.rotation-warning) {
  background: #fff7e6;
}

:deep(.rotation-severe) {
  color: #ff4d4f;
}

:deep(.rotation-level.rotation-severe) {
  background: #fff1f0;
}

.toric-error {
  padding: 10px;
  color: #f5222d;
  border: 1px solid #ffccc7;
  background: #fff2f0;
}

.toric-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin: 16px 0;
}

.bo-wrapper-pc {
  .ant-layout-header {
    height: 0.42rem;
    line-height: 0.42rem;
    padding-left: 0.52rem;
    padding-right: 0.28rem;
    font-size: 0.17rem;
  }
}

@media (min-width: 701px) {
  .toric-page {
    max-width: 1400px;
    margin: 9px auto;
  }

  .toric-heading {
    padding: 11px 18px;
  }

  .toric-patient {
    padding: 8px 14px;
  }

  .toric-input-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    padding: 10px 0;
  }

  .toric-card,
  .toric-analysis {
    padding: 10px 12px;
  }

  .card-title,
  .analysis-title,
  .rotation-title {
    margin-bottom: 8px;
  }

  .toric-row {
    gap: 6px;
    margin-bottom: 7px;
  }

  .toric-number-field {
    flex: 1;
    min-width: 0;
  }

  .toric-result-line,
  .pca-box {
    margin-top: 7px;
    padding: 6px 8px;
  }

  .combined-card {
    gap: 8px;
  }

  .combined-section + .combined-section {
    padding-top: 8px;
  }

  .residual-grid {
    margin: 10px 0;
  }
}

@media (max-width: 700px) {
  .toric-page {
    margin-top: 12px;
  }

  .toric-heading {
    padding: 14px;
  }

  .toric-heading h2 {
    font-size: 17px;
  }

  .toric-heading > span {
    display: none;
  }

  .toric-input-grid {
    grid-template-columns: 1fr;
  }

  .toric-row {
    flex-wrap: wrap;
  }

  .optical-inputs {
    grid-template-columns: 1fr;
  }

  .iol-summary {
    justify-content: center;
    gap: 12px;
  }

  .axis-thumb {
    margin-left: 0;
    width: 100%;
    justify-content: center;
  }

  .residual-grid {
    grid-template-columns: 1fr;
  }
}
</style>
